import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { applicationApi } from "../../services/applicationApi";
import { StatusBadge } from "../../components/StatusBadge";
import { useLocalization } from "../../hooks/useLocalization";
import { usePermissions } from "../../hooks/usePermissions";
import { formatApiErrorDetail } from "../../lib/api";
import { EMPLOYMENT_TYPES } from "../../lib/constants";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { AccessDenied } from "../../components/AccessDenied";
import { NotFoundState } from "../../components/NotFoundState";

const STEP_FIELDS = {
  identity: [
    { key: "legal_first_name", label: "Legal First Name" }, { key: "legal_last_name", label: "Legal Last Name" },
    { key: "personal_email", label: "Personal Email" }, { key: "phone", label: "Phone" },
    { key: "date_of_birth", label: "Date of Birth", type: "date" }, { key: "nationality", label: "Nationality" },
    { key: "address", label: "Address" }, { key: "japanese_name", label: "Japanese Name" }, { key: "katakana_name", label: "Katakana Name" },
  ],
  employment: [
    { key: "store_location", label: "Store Location" }, { key: "department", label: "Department" },
    { key: "position", label: "Position" }, { key: "employment_type", label: "Employment Type", type: "select", options: EMPLOYMENT_TYPES },
    { key: "proposed_start_date", label: "Proposed Start Date", type: "date" },
    { key: "expected_days_per_week", label: "Expected Days/Week", type: "number" },
    { key: "expected_hours_per_week", label: "Expected Hours/Week", type: "number" },
  ],
  compensation: [
    { key: "pay_basis", label: "Pay Basis" }, { key: "proposed_wage", label: "Proposed Wage", type: "number" },
    { key: "wage_effective_date", label: "Wage Effective Date", type: "date" }, { key: "payroll_frequency", label: "Payroll Frequency" },
  ],
  documents_required: [
    { key: "japanese_national", label: "Japanese National", type: "checkbox" }, { key: "permanent_resident", label: "Permanent Resident", type: "checkbox" },
    { key: "residence_card_required", label: "Residence Card Required", type: "checkbox" }, { key: "passport_required", label: "Passport Required", type: "checkbox" },
    { key: "work_authorization_required", label: "Work Authorization Required", type: "checkbox" }, { key: "my_number_required", label: "My Number Required", type: "checkbox" },
    { key: "bank_details_required", label: "Bank Details Required", type: "checkbox" }, { key: "emergency_contact_required", label: "Emergency Contact Required", type: "checkbox" },
  ],
  medical_facts: [
    { key: "expected_late_night_work", label: "Expected Late-Night Work", type: "checkbox" }, { key: "expected_opening_work", label: "Expected Opening Work", type: "checkbox" },
    { key: "expected_closing_work", label: "Expected Closing Work", type: "checkbox" }, { key: "food_handling", label: "Food Handling", type: "checkbox" },
    { key: "kitchen_work", label: "Kitchen Work", type: "checkbox" }, { key: "heavy_lifting", label: "Heavy Lifting", type: "checkbox" },
    { key: "other_special_duty", label: "Other Special Duty" },
  ],
  training: [{ key: "assigned_training_keys", label: "Training Keys (comma separated)", type: "list" }],
};

const STEP_KEYS = Object.keys(STEP_FIELDS);
const EDITABLE_STATUSES = ["DRAFT", "INFORMATION_REQUIRED"];

export default function ApplicationWizardPage() {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canManageOnboarding } = usePermissions();
  const [application, setApplication] = useState(null);
  const [activeStep, setActiveStep] = useState("identity");
  const [stepData, setStepData] = useState({});
  const [duplicates, setDuplicates] = useState(null);
  const [reason, setReason] = useState("");
  const [accessDenied, setAccessDenied] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(() => {
    applicationApi.get(applicationId)
      .then((r) => { setApplication(r.data); setStepData(r.data[activeStep] || {}); setAccessDenied(false); setNotFound(false); })
      .catch((err) => { if (err.response?.status === 403) setAccessDenied(true); else if (err.response?.status === 404) setNotFound(true); });
  }, [applicationId, activeStep]);
  useEffect(() => { load(); }, [load]);

  if (accessDenied) return <AccessDenied resourceLabel={t("applications.title")} />;
  if (notFound) return <NotFoundState testId="application-not-found" backTo="/management/onboarding" />;
  if (!application) return <p className="text-sm text-gray-500">{t("common.loading")}</p>;

  const editable = EDITABLE_STATUSES.includes(application.status);

  const handleSaveStep = async () => {
    const payload = { ...stepData };
    if (activeStep === "training" && typeof payload.assigned_training_keys === "string") {
      payload.assigned_training_keys = payload.assigned_training_keys.split(",").map((s) => s.trim()).filter(Boolean);
    }
    const { data } = await applicationApi.updateStep(applicationId, activeStep, payload);
    setApplication(data);
    toast.success(t("common.save"));
  };

  const handleSubmit = async () => {
    try {
      const { data } = await applicationApi.submit(applicationId);
      setApplication(data);
      if (data.duplicate_check?.status === "POSSIBLE_DUPLICATE") setDuplicates(data.duplicate_check.matches);
      toast.success(t("applications.submit"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleReviewAction = async (action) => {
    try {
      let data;
      if (action === "reject") ({ data } = await applicationApi.reject(applicationId, reason));
      else if (action === "requestCorrection") ({ data } = await applicationApi.requestCorrection(applicationId, reason));
      else if (action === "approveAndConvert") { const result = await applicationApi.approveAndConvert(applicationId); data = null; toast.success(t("applications.convertedTo")); navigate(`/management/employees/${result.data.employee.id}`); return; }
      setApplication(data);
      setReason("");
      toast.success(t("common.submit"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-3xl space-y-5" data-testid="application-wizard-page">
      <PageHelpBanner pageKey="application-wizard" title="How to use this page">
        <p>Fill each tab (Identity, Employment, Compensation, Documents Required, Medical Facts, Training) and save as you go - nothing is locked until submitted. Once submitted, use Request Correction/Reject/Approve &amp; Convert to move it toward becoming a real employee record.</p>
      </PageHelpBanner>
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold tracking-tight text-gray-900">{t("applications.title")}</h2>
        <StatusBadge status={application.status} />
      </div>

      <div className="flex flex-wrap gap-2">
        {STEP_KEYS.map((key) => (
          <button
            key={key}
            data-testid={`application-step-tab-${key}`}
            onClick={() => setActiveStep(key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
              activeStep === key ? "bg-blue-600 text-white border-blue-600" : "border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {t(`applications.steps.${key}`)}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3" data-testid="application-step-form">
        {STEP_FIELDS[activeStep].map((field) => (
          <div key={field.key}>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">{field.label}</label>
            {field.type === "checkbox" ? (
              <input data-testid={`application-field-${field.key}`} type="checkbox" disabled={!editable} checked={!!stepData[field.key]} onChange={(e) => setStepData((s) => ({ ...s, [field.key]: e.target.checked }))} />
            ) : field.type === "select" ? (
              <select data-testid={`application-field-${field.key}`} disabled={!editable} value={stepData[field.key] || ""} onChange={(e) => setStepData((s) => ({ ...s, [field.key]: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm">
                <option value="">-</option>
                {field.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            ) : field.type === "list" ? (
              <input data-testid={`application-field-${field.key}`} disabled={!editable} value={Array.isArray(stepData[field.key]) ? stepData[field.key].join(", ") : stepData[field.key] || ""} onChange={(e) => setStepData((s) => ({ ...s, [field.key]: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
            ) : (
              <input data-testid={`application-field-${field.key}`} type={field.type || "text"} disabled={!editable} value={stepData[field.key] ?? ""} onChange={(e) => setStepData((s) => ({ ...s, [field.key]: field.type === "number" ? Number(e.target.value) : e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
            )}
          </div>
        ))}
        {editable && (
          <button data-testid="application-save-step-button" onClick={handleSaveStep} className="px-5 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all">{t("common.save")}</button>
        )}
      </div>

      {duplicates && duplicates.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="application-duplicate-warning">
          <p className="text-sm font-semibold text-amber-900">{t("applications.duplicateWarning")}</p>
          {duplicates.map((d, i) => <p key={i} className="text-xs text-amber-800">{d.type} · {d.legal_name || d.id} · {(d.matched_fields || []).join(", ")}</p>)}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {application.status === "DRAFT" && (
          <button data-testid="application-submit-button" onClick={handleSubmit} className="px-5 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all">{t("applications.submit")}</button>
        )}
        {canManageOnboarding && ["SUBMITTED", "UNDER_MANAGEMENT_REVIEW", "INFORMATION_REQUIRED"].includes(application.status) && (
          <>
            <input data-testid="application-review-reason-input" placeholder={t("applications.reason")} value={reason} onChange={(e) => setReason(e.target.value)} className="px-4 py-2 rounded-lg border border-gray-300 text-sm" />
            <button data-testid="application-request-correction-button" disabled={!reason} onClick={() => handleReviewAction("requestCorrection")} className="px-4 py-2 rounded-full border border-amber-400 text-amber-700 text-sm font-semibold hover:bg-amber-50 disabled:opacity-40">{t("applications.requestCorrection")}</button>
            <button data-testid="application-reject-button" disabled={!reason} onClick={() => handleReviewAction("reject")} className="px-4 py-2 rounded-full border border-red-400 text-red-600 text-sm font-semibold hover:bg-red-50 disabled:opacity-40">{t("applications.reject")}</button>
            <button data-testid="application-approve-convert-button" onClick={() => handleReviewAction("approveAndConvert")} className="px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">{t("applications.approveAndConvert")}</button>
          </>
        )}
      </div>
    </div>
  );
}
